import { type ReactNode, createContext, useEffect, useState } from 'react'

import { type SanitySiteFragment } from '@data/sanity/queries/types/site'
import { type SanityProductCatalogueQuery } from '@data/sanity/queries/types/product'
import {
  type CountryCode,
  type CurrencyCode,
} from '@data/shopify/storefront/types'
import { type ShopifyShop, getShopifyShopAndCart } from './shopify/graphql/shop'
import {
  type ShopifyClient,
  getShopifyStorefrontClient,
} from './shopify/graphql/client'
import { getShopifyDomain, getShopifyStorefrontApiUrl } from './shopify/client'
import { type Locale } from './language'
import { getShopifyCartIdStorageKey } from './local-storage'
import { CartContextProvider } from './cart/context'
import { DiscountContextProvider } from './discount-context'
import { getLocaleVariable } from './environment'

export interface LocalizedShop {
  locale: Locale
  shop: ShopifyShop | null
}

interface ShopContextProps {
  taxRate: number
  shopId?: string
  currency?: string
  currencyCode?: CurrencyCode
  countryCode?: CountryCode
  shopifyDomain?: string
  shopifyPrimaryDomain?: string
  productCatalogue: SanityProductCatalogueQuery
  shopifyStorefrontClient?: ShopifyClient
}

interface ShopContextProviderProps {
  locale: Locale
  site: SanitySiteFragment
  productCatalogue: SanityProductCatalogueQuery
  children: ReactNode
}

export const ShopContext = createContext<ShopContextProps>({
  taxRate: 0,
  productCatalogue: [],
})

export const ShopContextProvider = ({
  locale,
  site,
  productCatalogue,
  children,
}: ShopContextProviderProps) => {
  const shopifyDomain = getShopifyDomain(locale)

  const [localeInitialised, setLocaleInitialised] = useState<Locale>()
  const [shopId, setShopId] = useState<string>()
  const [currency, setCurrency] = useState<string>()
  const [currencyCode, setCurrencyCode] = useState<CurrencyCode>()
  const [countryCode, setCountryCode] = useState<CountryCode>()
  const [shopifyPrimaryDomain, setShopifyPrimaryDomain] = useState<
    string | undefined
  >(shopifyDomain)
  const [localizedShop, setLocalizedShop] = useState<LocalizedShop>({
    locale,
    shop: null,
  })

  const shopifyStorefrontAccessToken = getLocaleVariable(
    locale,
    'NEXT_PUBLIC_SHOPIFY_API_TOKEN',
  )
  const shopifyStorefrontApiUrl = shopifyDomain
    ? getShopifyStorefrontApiUrl(shopifyDomain)
    : undefined
  const shopifyStorefrontClient =
    shopifyStorefrontApiUrl && shopifyStorefrontAccessToken
      ? getShopifyStorefrontClient(
          shopifyStorefrontApiUrl,
          shopifyStorefrontAccessToken,
        )
      : undefined

  // Get Shopify shop and cart information (update when switching language)
  useEffect(() => {
    if (localeInitialised === locale) {
      return
    }

    setLocalizedShop({
      locale,
      shop: null,
    })

    const loadShop = async () => {
      if (!shopifyStorefrontClient) {
        return
      }

      setLocaleInitialised(locale)

      const shopifyCartIdStorageKey = getShopifyCartIdStorageKey(locale)
      const cartId = localStorage.getItem(shopifyCartIdStorageKey)
      const shopifyShop = await getShopifyShopAndCart(
        shopifyStorefrontClient,
        cartId,
      )
      setLocalizedShop({
        locale,
        shop: shopifyShop ?? null,
      })
    }

    loadShop()
  }, [localeInitialised, locale, shopifyStorefrontClient])

  useEffect(() => {
    if (!localizedShop.shop) {
      return
    }

    setShopId(localizedShop.shop?.shop?.id)
    // Remove HTML tags and value placeholder "{{amount}}" from currency format string
    setCurrency(
      localizedShop.shop?.shop?.moneyFormat
        ?.replace(/<\/?[^>]+(>|$)/g, '')
        ?.replace(/\s*[{]+.*?[}]+\s*/g, ''),
    )
    setCurrencyCode(localizedShop.shop?.shop?.paymentSettings?.currencyCode)
    setCountryCode(localizedShop.shop?.shop?.paymentSettings?.countryCode)
    setShopifyPrimaryDomain(
      localizedShop.shop?.shop?.primaryDomain?.host ?? shopifyDomain,
    )
  }, [localizedShop.shop, shopifyDomain])

  // TODO: ShopifyContext.
  return (
    <ShopContext.Provider
      value={{
        taxRate: site.cartSettings.taxRate ?? 0,
        shopId,
        currency,
        currencyCode,
        countryCode,
        shopifyDomain,
        shopifyPrimaryDomain,
        productCatalogue,
        shopifyStorefrontClient,
      }}
    >
      <CartContextProvider
        localizedShop={localizedShop}
        site={site}
        locale={locale}
      >
        <DiscountContextProvider site={site}>
          {children}
        </DiscountContextProvider>
      </CartContextProvider>
    </ShopContext.Provider>
  )
}
